import {
  Button,
  FlexBox,
  Modal,
  space,
  theme,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styledComponents from "styled-components";

import type { Room } from "../view-model";

const Separator = styledComponents.div`
  border-top: 1px solid #e0e0e0;
  margin: ${space(2)} 0;
`;

const StyledLabel = styledComponents.label`
  font-size: 14px;
  font-weight: 500;
  width: 120px;
  text-align: left;
  padding-right: ${space(3)};
`;

const StyledInput = styledComponents.input`
  padding: ${space(2)};
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  flex: 1;
  &:focus {
    outline: none;
    border-color: #0066cc;
  }
  &::placeholder {
    color: ${theme.colorPalette.grey400};
  }
`;

const StyledTextArea = styledComponents.textarea`
  padding: ${space(2)};
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  flex: 1;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: #0066cc;
  }
  &::placeholder {
    color: ${theme.colorPalette.grey400};
  }
`;

type RoomEditModalProps = {
  room: Room | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedRoom: Room) => void;
};

export function RoomEditModal({
  isOpen,
  onClose,
  onSave,
  room,
}: RoomEditModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Partial<Room>>({});

  // Initialize form data when room changes
  useEffect(() => {
    if (room) {
      setFormData({
        name: room.name,
        roomNumber: room.roomNumber,
        contactPerson: room.contactPerson,
        contactData: room.contactData,
      });
    }
  }, [room]);

  function handleChange(field: string, value: string) {
    setFormData({
      ...formData,
      [field]: value,
    });
  }

  function handleSubmit() {
    if (room) {
      onSave({
        ...room,
        ...formData,
      });
    }
    onClose();
  }

  if (!room) return null;

  return (
    <Modal onClose={onClose} open={isOpen} width="500px">
      <FlexBox paddingBottom={space(4)}>
        <Typography size="xl" weight="semiBold">
          {t("room.edit")}
        </Typography>
      </FlexBox>
      <Separator />
      <FlexBox alignItems="right" gap={space(8)} marginBottom={space(8)}>
        <Typography color="grey400" size="sm">
          {room.id}
        </Typography>
      </FlexBox>

      <FlexBox flexDirection="column" gap={space(3)} marginBottom={space(8)}>
        <FlexBox alignItems="center" gap={space(8)}>
          <StyledLabel>{t("room.number")}</StyledLabel>
          <Typography color="grey400">{room.roomNumber}</Typography>
        </FlexBox>
        <FlexBox alignItems="center" gap={space(8)}>
          <StyledLabel>{t("room.name")}</StyledLabel>
          <StyledInput
            onChange={(e) => handleChange("name", e.target.value)}
            type="text"
            value={formData.name ?? ""}
          />
        </FlexBox>

        <FlexBox alignItems="center" gap={space(8)}>
          <StyledLabel>{t("room.contactPerson")}</StyledLabel>
          <StyledInput
            onChange={(e) => handleChange("contactPerson", e.target.value)}
            type="text"
            value={formData.contactPerson ?? ""}
          />
        </FlexBox>

        <FlexBox alignItems="center" gap={space(8)}>
          <StyledLabel>{t("room.contactData")}</StyledLabel>
          <StyledTextArea
            onChange={(e) => handleChange("contactData", e.target.value)}
            rows={4}
            value={formData.contactData ?? ""}
          />
        </FlexBox>
      </FlexBox>

      <FlexBox gap={space(2)} justifyContent="flex-end" padding={space(4)}>
        <Button onClick={onClose} variant="secondary">
          {t("common.close")}
        </Button>
        <Button onClick={handleSubmit}>{t("common.save")}</Button>
      </FlexBox>
    </Modal>
  );
}
